import React, { useMemo, useState } from 'react';
import AdminLayout from 'cms/core/shared/AdminLayout';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { JobHttpService } from 'cms/features/feature_job/service';
import { Avatar, Box, CircularProgress, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Tooltip } from '@mui/material';
import {
  getProfileAvatarUrl,
  getProfileDisplayName,
  getProfileAvatarLetter,
  nameToAvatarColor,
} from 'features/feature_job/utils/profileDisplay';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import ListToolbar from 'cms/components/ListToolbar';

const ApplicationsScreen = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading, isError, isFetching, refetch } = useQuery(['cms_applications'], () =>
    JobHttpService.loadJobApplications({ page: 1, size: 100 })
  );

  const deleteMutation = useMutation(
    (id) => JobHttpService.deleteApplication(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['cms_applications']);
        setDeleteTarget(null);
      },
    }
  );

  const rows = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return data.results ?? [];
  }, [data]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => {
      const profile = row.user?.profile;
      const name = `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.toLowerCase();
      return (
        String(row.id).includes(term) ||
        (row.job?.title ?? '').toLowerCase().includes(term) ||
        (row.user?.email ?? '').toLowerCase().includes(term) ||
        name.includes(term)
      );
    });
  }, [rows, searchTerm]);

  return (
    <AdminLayout pageTitle="Job Applications">
      <Stack spacing={2}>
        <ListToolbar
          title="Job Applications"
          subtitle={`${filteredRows.length} application${filteredRows.length === 1 ? '' : 's'}`}
          search={{ value: searchTerm, onChange: setSearchTerm, placeholder: 'Search by job, applicant, email…' }}
          onRefresh={() => refetch()}
          refreshing={isFetching}
        />

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Typography color="error">Failed to load applications.</Typography>
        )}

        {!isLoading && !isError && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Job</TableCell>
                  <TableCell>Applicant</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">No applications found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {filteredRows.map((row) => {
                  const applicantUser = row.user;
                  const applicantProfile = applicantUser?.profile;
                  const displayName = applicantUser
                    ? getProfileDisplayName(applicantProfile, applicantUser)
                    : '-';
                  const avatarSrc = applicantUser ? getProfileAvatarUrl(applicantProfile) : null;
                  const showLetter = applicantUser && displayName !== '-';

                  return (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.job?.title ?? '-'}</TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar
                          src={avatarSrc || undefined}
                          alt={displayName === '-' ? 'Applicant' : displayName}
                          sx={{
                            width: 40,
                            height: 40,
                            fontSize: '1rem',
                            fontWeight: 700,
                            bgcolor: nameToAvatarColor(displayName === '-' ? '?' : displayName),
                          }}
                        >
                          {showLetter ? getProfileAvatarLetter(applicantProfile, applicantUser) : '?'}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {displayName}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{row.user?.email ?? '-'}</TableCell>
                    <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton color="primary" onClick={() => navigate(`/cms/applications/${row.id}`)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => setDeleteTarget(row)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Application</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete application #{deleteTarget?.id}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteMutation.mutate(deleteTarget?.id)}
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default ApplicationsScreen;


